import type { WithT } from 'i18next';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';

import type { GrowthCreditEntry, IMediaRedemptionGrowthCreditEntry } from '@feathr/blackbox';
import { EGrowthCreditEntryClass } from '@feathr/blackbox';
import { type IColumn, Skeleton, TableColumnHeader, Text } from '@feathr/components';
import GrowthCreditIcon from '@feathr/extender/components/GrowthCreditIcon/GrowthCreditIcon';
import { useStore } from '@feathr/extender/state';
import { moment, TimeFormat } from '@feathr/hooks';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';
import * as styles from './creditsRedeemedColumns.css';

const CREDITS_TO_DOLLAR_RATE = 100;

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

interface IProjectCellProps {
  projectId: string;
}

function ProjectCell({ projectId }: Readonly<IProjectCellProps>): JSX.Element {
  const { Events } = useStore();
  const [projectName, setProjectName] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchProject(): Promise<void> {
      try {
        const project = Events.get(projectId);
        await project.reload();
        setProjectName(project.name);
      } catch {
        setProjectName(undefined);
      } finally {
        setIsLoading(false);
      }
    }

    fetchProject();
  }, [Events, projectId]);

  if (isLoading) {
    return <Skeleton width={100} />;
  }

  return <Text.DefaultRegular truncate={false}>{projectName ?? '—'}</Text.DefaultRegular>;
}

export function creditsRedeemedColumns({ t }: WithT): Array<IColumn<GrowthCreditEntry>> {
  return [
    {
      id: 'date_entered',
      Header: TableColumnHeader({
        sortType: 'alpha',
        title: t('Transaction date'),
      }),
      headerClassName: tableStyles.sort,
      sortable: true,
      Cell({ original }): JSX.Element {
        return (
          <Text.DefaultMedium>
            {moment(original.get('date_entered')).format(TimeFormat.shortDate)}
          </Text.DefaultMedium>
        );
      },
    },
    {
      id: 'credits',
      Header: TableColumnHeader({
        sortType: 'numeric',
        title: t('Credits Redeemed'),
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      sortable: true,
      Cell({ original }): JSX.Element {
        const credits = original.get('credits');
        return (
          <div className={styles.creditsCell}>
            <Text.DefaultRegular>{Math.abs(credits).toLocaleString()}</Text.DefaultRegular>
            <GrowthCreditIcon />
          </div>
        );
      },
    },
    {
      id: 'value',
      Header: TableColumnHeader({
        sortType: 'numeric',
        title: t('Value'),
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      sortable: false,
      Cell({ original }): JSX.Element {
        const credits = Math.abs(original.get('credits'));
        const value = credits / CREDITS_TO_DOLLAR_RATE;
        return <Text.DefaultRegular>{formatCurrency(value)}</Text.DefaultRegular>;
      },
    },
    {
      id: 'project',
      Header: TableColumnHeader({
        sortType: 'alpha',
        title: t('Project'),
      }),
      headerClassName: tableStyles.sort,
      sortable: false,
      Cell({ original }): JSX.Element {
        const cls = original.get('_cls');
        if (cls === EGrowthCreditEntryClass.MediaRedemption) {
          const projectId = (original.attributes as IMediaRedemptionGrowthCreditEntry).project;
          if (projectId) {
            return <ProjectCell projectId={projectId} />;
          }
        }
        return <Text.DefaultRegular>—</Text.DefaultRegular>;
      },
    },
  ];
}
